import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Chip,
  Grid,
  Card,
  CardContent,
  InputAdornment,
  CircularProgress,
  List,
  ListItemText,
  ListItemButton,
  Divider,
  Badge
} from '@mui/material';
import {
  Search as SearchIcon,
  Inventory as InventoryIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import { InventoryItem, Category as CategoryType } from '../../utils/types';
import {
  searchInventoryItems,
  getAllInventoryItems
} from '../../services/inventoryService';
import { getAllCategories, getSubcategories } from '../../services/categoryService';
import { useCurrency } from '../../hooks/useCurrency';

interface EnhancedPartsSelectorProps {
  open: boolean;
  onClose: () => void;
  onSelectPart: (part: InventoryItem, quantity: number) => void;
  warehouseId?: string;
}

// استخدام نوع Category من utils/types
type Category = CategoryType;

// تعريف SubCategory محلياً مع parentId اختياري
interface SubCategory {
  id: string;
  name: string;
  parentId?: string;
  description?: string;
  imageUrl?: string;
  isActive: boolean;
  order?: number;
  createdAt: Date | string;
  updatedAt: Date | string;
}

const EnhancedPartsSelector: React.FC<EnhancedPartsSelectorProps> = ({
  open,
  onClose,
  onSelectPart,
  warehouseId
}) => {
  // States
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedSubcategory, setSelectedSubcategory] = useState<string>('');
  const [selectedPart, setSelectedPart] = useState<InventoryItem | null>(null);
  const [quantity, setQuantity] = useState<number>(1);
  const [loading, setLoading] = useState(false);
  const [searchResults, setSearchResults] = useState<InventoryItem[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [subcategories, setSubcategories] = useState<SubCategory[]>([]);
  const [allParts, setAllParts] = useState<InventoryItem[]>([]);

  // استخدام hook العملة
  const { formatCurrency } = useCurrency();

  // Load categories on mount
  useEffect(() => {
    loadCategories();
    loadAllParts();
  }, []);

  // Load subcategories when category changes
  useEffect(() => {
    if (selectedCategory) {
      loadSubcategories(selectedCategory);
      setSelectedSubcategory('');
    } else {
      setSubcategories([]);
      setSelectedSubcategory('');
    }
  }, [selectedCategory]);

  // Perform search when query changes
  useEffect(() => {
    if (searchQuery.trim()) {
      performSearch();
    } else {
      setSearchResults([]);
    }
  }, [searchQuery]);

  // Load categories
  const loadCategories = async () => {
    try {
      const categoriesData = await getAllCategories();
      // Filter main categories only (no parentId)
      const mainCategories = categoriesData.filter(cat => !cat.parentId);
      setCategories(mainCategories);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  // Load subcategories
  const loadSubcategories = async (categoryId: string) => {
    try {
      const subcategoriesData = await getSubcategories(categoryId);
      // تحويل البيانات إلى النوع المطلوب
      const convertedSubcategories: SubCategory[] = subcategoriesData.map(sub => ({
        id: sub.id,
        name: sub.name,
        parentId: sub.parentId,
        description: sub.description,
        imageUrl: sub.imageUrl,
        isActive: sub.isActive,
        order: sub.order,
        createdAt: sub.createdAt,
        updatedAt: sub.updatedAt
      }));
      setSubcategories(convertedSubcategories);
    } catch (error) {
      console.error('Error loading subcategories:', error);
      setSubcategories([]);
    }
  };

  // Load all parts
  const loadAllParts = async () => {
    try {
      setLoading(true);
      const parts = await getAllInventoryItems();
      setAllParts(parts);
    } catch (error) {
      console.error('Error loading all parts:', error);
    } finally {
      setLoading(false);
    }
  };

  // Perform search
  const performSearch = async () => {
    try {
      setLoading(true);
      const results = await searchInventoryItems(searchQuery);
      setSearchResults(results);
    } catch (error) {
      console.error('Error searching parts:', error);
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  // Get filtered parts based on category/subcategory
  const filteredParts = useMemo(() => {
    if (searchQuery.trim()) {
      return searchResults;
    }

    let parts = allParts;

    if (selectedSubcategory) {
      // Find subcategory name
      const subcategory = subcategories.find(sub => sub.id === selectedSubcategory);
      if (subcategory) {
        parts = parts.filter(part => part.category === subcategory.name);
      }
    } else if (selectedCategory) {
      // Find category name
      const category = categories.find(cat => cat.id === selectedCategory);
      if (category) {
        parts = parts.filter(part => part.category === category.name);
      }
    }

    return parts;
  }, [allParts, searchResults, selectedCategory, selectedSubcategory, searchQuery, categories, subcategories]);

  // Clear filters
  const clearFilters = () => {
    setSearchQuery('');
    setSelectedCategory('');
    setSelectedSubcategory('');
    setSearchResults([]);
  };

  // Handle part selection
  const handlePartSelect = (part: InventoryItem) => {
    setSelectedPart(part);
  };

  // Handle add part
  const handleAddPart = () => {
    if (selectedPart && quantity > 0) {
      onSelectPart(selectedPart, quantity);
      // Reset form
      setSelectedPart(null);
      setQuantity(1);
      onClose();
    }
  };

  // Handle close
  const handleClose = () => {
    setSelectedPart(null);
    setQuantity(1);
    clearFilters();
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { height: '80vh' }
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <InventoryIcon />
          اختيار قطعة غيار
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Grid container spacing={2}>
          {/* Search and Filters */}
          <Grid item xs={12}>
            <Box mb={2}>
              {/* Search Bar */}
              <TextField
                fullWidth
                placeholder="البحث عن قطعة غيار..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery && (
                    <InputAdornment position="end">
                      <Button
                        size="small"
                        onClick={() => setSearchQuery('')}
                        sx={{ minWidth: 'auto', p: 0.5 }}
                      >
                        <ClearIcon fontSize="small" />
                      </Button>
                    </InputAdornment>
                  )
                }}
                sx={{ mb: 2 }}
              />

              {/* Category Filters */}
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>الفئة الرئيسية</InputLabel>
                    <Select
                      value={selectedCategory}
                      label="الفئة الرئيسية"
                      onChange={(e) => setSelectedCategory(e.target.value)}
                    >
                      <MenuItem value="">جميع الفئات</MenuItem>
                      {categories.map((category) => (
                        <MenuItem key={category.id} value={category.id}>
                          {category.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth disabled={!selectedCategory}>
                    <InputLabel>الفئة الفرعية</InputLabel>
                    <Select
                      value={selectedSubcategory}
                      label="الفئة الفرعية"
                      onChange={(e) => setSelectedSubcategory(e.target.value)}
                    >
                      <MenuItem value="">جميع الفئات الفرعية</MenuItem>
                      {subcategories.map((subcategory) => (
                        <MenuItem key={subcategory.id} value={subcategory.id}>
                          {subcategory.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              {/* Active Filters */}
              {(searchQuery || selectedCategory || selectedSubcategory) && (
                <Box mt={2} display="flex" gap={1} alignItems="center" flexWrap="wrap">
                  <Typography variant="body2" color="text.secondary">
                    الفلاتر النشطة:
                  </Typography>
                  {searchQuery && (
                    <Chip
                      label={`البحث: ${searchQuery}`}
                      onDelete={() => setSearchQuery('')}
                      size="small"
                      color="primary"
                    />
                  )}
                  {selectedCategory && (
                    <Chip
                      label={`الفئة: ${categories.find(c => c.id === selectedCategory)?.name}`}
                      onDelete={() => setSelectedCategory('')}
                      size="small"
                      color="secondary"
                    />
                  )}
                  {selectedSubcategory && (
                    <Chip
                      label={`الفئة الفرعية: ${subcategories.find(s => s.id === selectedSubcategory)?.name}`}
                      onDelete={() => setSelectedSubcategory('')}
                      size="small"
                      color="info"
                    />
                  )}
                  <Button
                    size="small"
                    onClick={clearFilters}
                    startIcon={<ClearIcon />}
                  >
                    مسح الكل
                  </Button>
                </Box>
              )}
            </Box>
          </Grid>

          {/* Parts List */}
          <Grid item xs={12} md={8}>
            <Box 
              sx={{ 
                height: '400px', 
                overflowY: 'auto',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                p: 1
              }}
            >
              {loading ? (
                <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                  <CircularProgress />
                </Box>
              ) : filteredParts.length === 0 ? (
                <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                  <Typography color="text.secondary">
                    {searchQuery ? 'لا توجد نتائج للبحث' : 'لا توجد قطع غيار متاحة'}
                  </Typography>
                </Box>
              ) : (
                <List dense>
                  {filteredParts.map((part) => (
                    <React.Fragment key={part.id}>
                      <ListItemButton
                        selected={selectedPart?.id === part.id}
                        onClick={() => handlePartSelect(part)}
                      >
                        <ListItemText
                          primary={
                            <Box display="flex" justifyContent="space-between" alignItems="center">
                              <Typography variant="subtitle2">
                                {part.name}
                              </Typography>
                              <Badge 
                                badgeContent={part.quantity} 
                                color={part.quantity > 0 ? 'success' : 'error'}
                                max={999}
                              >
                                <InventoryIcon fontSize="small" />
                              </Badge>
                            </Box>
                          }
                          secondary={
                            <Box>
                              <Typography variant="body2" color="text.secondary">
                                الفئة: {part.category || 'غير محدد'}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                السعر: {formatCurrency(part.sellingPrice || part.purchasePrice || 0)}
                              </Typography>
                              {part.description && (
                                <Typography variant="caption" color="text.secondary">
                                  {part.description}
                                </Typography>
                              )}
                            </Box>
                          }
                        />
                      </ListItemButton>
                      <Divider />
                    </React.Fragment>
                  ))}
                </List>
              )}
            </Box>
          </Grid>

          {/* Selected Part Details */}
          <Grid item xs={12} md={4}>
            {selectedPart ? (
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    تفاصيل القطعة المختارة
                  </Typography>
                  <Typography variant="subtitle1" color="primary" gutterBottom>
                    {selectedPart.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    الفئة: {selectedPart.category || 'غير محدد'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    الكمية المتاحة: {selectedPart.quantity}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    سعر البيع: {formatCurrency(selectedPart.sellingPrice || 0)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    سعر الشراء: {formatCurrency(selectedPart.purchasePrice || 0)}
                  </Typography>
                  {selectedPart.description && (
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      الوصف: {selectedPart.description}
                    </Typography>
                  )}
                  
                  <Box mt={2}>
                    <TextField
                      label="الكمية المطلوبة"
                      type="number"
                      fullWidth
                      value={quantity}
                      onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                      InputProps={{
                        inputProps: { 
                          min: 1, 
                          max: selectedPart.quantity 
                        }
                      }}
                      helperText={`الحد الأقصى: ${selectedPart.quantity}`}
                    />
                  </Box>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent>
                  <Typography variant="body2" color="text.secondary" textAlign="center">
                    اختر قطعة غيار لعرض التفاصيل
                  </Typography>
                </CardContent>
              </Card>
            )}
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>
          إلغاء
        </Button>
        <Button
          onClick={handleAddPart}
          variant="contained"
          disabled={!selectedPart || quantity <= 0 || quantity > (selectedPart?.quantity || 0)}
        >
          إضافة قطعة الغيار
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EnhancedPartsSelector;
